import axios from 'axios';
import type {
  SelectArtistAccountPayload,
  SelectArtistAccountResponse,
  SignInResponse,
  SignOutResponse,
  UnselectArtistAccountResponse,
} from '@/apis/typed';

const API_ORIGIN = process.env.NEXT_PUBLIC_API_ORIGIN;

const axiosInstance = axios.create({
  baseURL: `${API_ORIGIN}/v1`,
  withCredentials: true,
});

let authHeaders: Record<string, string> = {};
// authHeaders(Google ID トークン)の失効時刻(UNIX ミリ秒)。この時刻を過ぎたら再取得する。
let authHeadersExpiresAt = 0;
// トークン失効の何ミリ秒前に再取得するかの余裕。
const AUTH_HEADERS_REFRESH_BUFFER_MS = 5 * 60 * 1000;
// exp をデコードできなかった場合のフォールバック TTL(Google ID トークンの寿命は約1時間)。
const AUTH_HEADERS_FALLBACK_TTL_MS = 50 * 60 * 1000;

/** `Authorization: Bearer <JWT>` から exp(UNIX 秒)を取り出す。失敗したら undefined。 */
const parseTokenExpiryMs = (headers: Record<string, string>): number | undefined => {
  const token = headers.Authorization?.replace(/^Bearer\s+/i, '');
  const payload = token?.split('.')[1];
  if (!payload) return undefined;
  try {
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof json.exp === 'number' ? json.exp * 1000 : undefined;
  } catch {
    return undefined;
  }
};

const fetchAuthHeaders = async () => {
  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL}/frontend-api/google-auth`, {
      cache: 'no-store',
    });
    authHeaders = await response.json();
    const expMs = parseTokenExpiryMs(authHeaders);
    authHeadersExpiresAt = expMs ?? Date.now() + AUTH_HEADERS_FALLBACK_TTL_MS;
  } catch (error) {
    console.error('Failed to fetch auth headers:', error);
    throw error;
  }
};

axiosInstance.interceptors.request.use(
  async (config) => {
    // 未取得、または失効(の 5 分前)を過ぎていれば再取得する。
    // クライアント側での IAM トークン付与は local のみ（デプロイ環境は基盤側で付与する）。
    const isStale = Date.now() >= authHeadersExpiresAt - AUTH_HEADERS_REFRESH_BUFFER_MS;
    if (
      isStale &&
      process.env.NEXT_PUBLIC_ENABLE_IAM === 'true' &&
      process.env.NEXT_PUBLIC_ENVIRONMENT === 'local'
    ) {
      try {
        await fetchAuthHeaders();
      } catch (error) {
        // 前倒し再取得が一時的な失敗をした場合、まだ有効なキャッシュが残っていれば
        // それで続行し、キャッシュが空のときだけ失敗させる（ネットワーク断で全リクエストを落とさない）。
        if (Object.keys(authHeaders).length === 0) throw error;
      }
    }

    // @ts-expect-error 型ERRORの解消
    config.headers = {
      ...config.headers,
      ...authHeaders,
    };
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

const addAuthHeader = (token?: string) =>
  token ? { headers: { 'X-Klew-Authorization': `Bearer ${token}` } } : {};

export const PostSignInAPI = async (token?: string): Promise<SignInResponse> => {
  try {
    const res = await axiosInstance.post<SignInResponse>('signin', {}, addAuthHeader(token));
    return res.data;
  } catch (error) {
    console.error('SignIn API error:', error);
    throw error;
  }
};

export const PostSignOutAPI = async (token?: string): Promise<SignOutResponse> => {
  try {
    const res = await axiosInstance.post<SignOutResponse>('signout', {}, addAuthHeader(token));
    return res.data;
  } catch (error) {
    console.error('SignOut API error:', error);
    throw error;
  }
};

/**
 * アーティストオフィシャルアカウント選択APIを実行する
 *
 * TODO: 以下のドキュメントはエンドポイントが古いままのため、新しくなり次第リンクを更新する
 * @see https://kulture-klew-auth-openapi.vercel.app/#operation/post-v1-artist-user-select
 */
export const PostSelectArtistAccountAPI = async ({
  artistOfficialAccountId,
  channelId,
  token,
}: SelectArtistAccountPayload): Promise<SelectArtistAccountResponse> => {
  try {
    const res = await axiosInstance.post<SelectArtistAccountResponse>(
      'artist/official/select',
      {
        artistOfficialAccountId,
        channelId,
      },
      addAuthHeader(token),
    );
    return res.data;
  } catch (error) {
    console.error('Select Artist Account API error:', error);
    throw error;
  }
};

/**
 * アーティストオフィシャルアカウント選択解除APIを実行する
 *
 * TODO: 以下のドキュメントはエンドポイントが古いままのため、新しくなり次第リンクを更新する
 * @see https://kulture-klew-auth-openapi.vercel.app/#operation/post-v1-artist-user-unselect
 */
export const PostUnselectArtistAccountAPI = async (
  token?: string,
): Promise<UnselectArtistAccountResponse> => {
  try {
    const res = await axiosInstance.post<UnselectArtistAccountResponse>(
      'artist/official/unselect',
      {},
      addAuthHeader(token),
    );
    return res.data;
  } catch (error) {
    console.error('Unselect Artist Account API error:', error);
    throw error;
  }
};
